import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import AppState from '@webapp/store/state';
import { trackList } from '@webapp/store/tracks/selectors';
import { blockEditor } from '@webapp/store/release/selectors';
import { UpdateTrackField } from '@webapp/store/tracks/action-creators';

import { isISRC } from '@webapp/utils/validators';

import { getTrackIdFromPath } from '@webapp/utils/tracks/get-track-id-from-url-path';

import InputField from '@components/form/input';
import AnimatedHintCtrl from '@webapp/controllers/animated-hint';
import ErrorMessage from '@webapp/components/basics/error-message/index';

interface ISRCCtrlProps {
  className?: string;
  trackId: string;
  validate?: boolean;
}

interface StoreProps {
  code: string;
  isPreviousRelease: boolean;
  onChange: (value: string) => UpdateTrackField<'isrc'>;
  repeatedIsrc: boolean;
  validIsrc: boolean;
  isPublished: boolean;
}

const mapStateToProps = (state: AppState, props: ISRCCtrlProps) => {
  const activeTrackId = getTrackIdFromPath(state);
  const currentTrack = state.tracks.tracks[activeTrackId];
  const tracksWithCurrentIsrc = trackList(
    state.tracks.tracks,
    state.release.release?.id
  ).filter((track) => track.isrc === currentTrack?.isrc);

  return {
    code: state.tracks.tracks[props.trackId].isrc || '',
    isPreviousRelease: state.release.release?.releaseHistory === 'previous',
    repeatedIsrc: currentTrack?.isrc ? tracksWithCurrentIsrc.length > 1 : false,
    validIsrc: currentTrack?.isrc ? isISRC(currentTrack?.isrc) : true,
    isPublished:
      blockEditor(state) ||
      state.release.release?.isPublished ||
      state.release.release?.status === 'taken-down' ||
      state.release.release?.status === 'pending-processing' ||
      (currentTrack?.isrcExists ?? false),
  };
};

const mapDispatchToProps = (dispatch: Dispatch, props: ISRCCtrlProps) => ({
  onChange: (value: string) =>
    dispatch(new UpdateTrackField<'isrc'>(props.trackId, 'isrc', value)),
});

export const ISRCCtrl: React.FC<ISRCCtrlProps & StoreProps> = ({
  validate = false,
  repeatedIsrc,
  validIsrc,
  code,
  onChange,
  ...props
}) => {
  const [customCode, setCustomCode] = React.useState(code || '');

  React.useEffect(() => {
    setCustomCode(code.toUpperCase().replace(/ /g, ''));
  }, [code]);

  React.useEffect(() => {
    if (!customCode) {
      return;
    }

    const validateISRC = () => {
      if (customCode && !isISRC(customCode)) {
        return;
      } else {
        onChange(customCode);
      }
    };

    const timer = setTimeout(() => {
      validateISRC();
    }, 800);

    return () => clearTimeout(timer);
  }, [customCode, onChange]);

  const handleValidateISRC = (value: string) => {
    setCustomCode(value.toUpperCase().replace(/ /g, ''));
  };

  return (
    <div className={props.className}>
      <InputField
        label={<Translate id="RELEASE.TRACK_INFO.TRACK.ISRC.LABEL" />}
        type="text"
        value={customCode}
        onChange={(value) => handleValidateISRC(value)}
        locked={props.isPublished}
        customBorderRadius="4px 4px 0 0"
      />
      {customCode.length === 0 && props.isPreviousRelease && (
        <AnimatedHintCtrl noTitle noBorderTop align="left" noMargin>
          <Translate id="RELEASE.TRACK_INFO.TRACK.ISRC.HINT.PREVIOUS_RELEASE" />
        </AnimatedHintCtrl>
      )}
      {customCode.length === 0 && !props.isPreviousRelease && (
        <AnimatedHintCtrl noTitle noBorderTop align="left" noMargin>
          <Translate id="RELEASE.TRACK_INFO.TRACK.ISRC.HINT.NEW_RELEASE" />
        </AnimatedHintCtrl>
      )}
      {customCode.length > 0 && !isISRC(customCode) && (
        <AnimatedHintCtrl noTitle noBorderTop align="left" noMargin>
          <Translate id="RELEASE.TRACK_INFO.TRACK.ISRC.INVALID" />
        </AnimatedHintCtrl>
      )}
      {validate && (
        <ErrorMessage
          className="mt-5"
          hasBackground
          errorId={
            !validIsrc
              ? 'RELEASE.TRACK_INFO.TRACK.ISRC.INVALID'
              : repeatedIsrc
              ? 'RELEASE.TRACK_INFO.TRACK.ISRC.REPEATED'
              : undefined
          }
        />
      )}
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ISRCCtrl);
